import {
  getPrintOrderSettings,
  getPrintTimesheetSalarySettings,
  getPrintRevenueSettings,
} from '../redux/SettingsCRUD'
import {getLocalLogoUrl} from '@/modules/organizations/utils/logoUtils'
import {RevenueOrderItem, TimesheetModel} from '@/modules/timekeepings/models/TimesheetModel'
import { PrintTimesheetSalarySettings } from '../types/salary'
import { PrintRevenueSettings } from '../templates/defaultPrintReVenueSettings'
import { PrintOrderSettings } from '../types/printOrder'
import { formatIntegerNumber } from '@/modules/commons/utils/numberFormat'

export type PrintOrderData = {
  [key: string]: any
  order_code?: string
  customer?: {
    name?: string
    phone?: string
    address?: string | {
      address_line?: string
      ward?: string | { name?: string }
      province?: string | { name?: string }
      free_address?: string
      full_address?: string
    }
  }
  delivery_address?: {
    address_line?: string
    ward?: string | { name?: string }
    province?: string | { name?: string }
    free_address?: string
    full_address?: string
  }
  sales_employee?: {
    name?: string
    nickname?: string
  }
  company?: {
    name?: string
    code?: string
    company_type_display?: string
    logo?: string
    address?: string
    phone?: string
    email?: string
  }
  items?: Array<{
    item_code?: string
    order_code?: string
    product_name: string
    color: string
    quantity: number
    unit_name: string
    unit_price: number
    total_price: number
    note?: string
    isGroup?: boolean
    groupName?: string
  }>
  grand_total?: number
}

const formatCurrency = (amount: number | undefined) => {
  if (!amount) return '0'
  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

const linesToHtml = (items: string[], prefix: string) =>
  items
    .map((item, index) => `<div>${prefix.replace('{index}', String(index + 1))}${escapeHtml(item)}</div>`)
    .join('')

const resolveAddressPart = (value: unknown): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    const record = value as { name?: unknown; full_address?: unknown; address_line?: unknown }
    return String(record.name || record.full_address || record.address_line || '')
  }
  return String(value)
}

const formatOrderAddress = (orderData: PrintOrderData): string => {
  const address = orderData.delivery_address || orderData.customer?.address

  if (address && typeof address === 'object') {
    const parts = [
      address.free_address,
      address.address_line,
      address.full_address,
      resolveAddressPart(address.ward),
      resolveAddressPart(address.province),
    ].filter((part, index, array) => {
      const value = String(part || '').trim()
      if (!value) return false
      return array.findIndex((item) => String(item || '').trim() === value) === index
    })

    if (parts.length > 0) {
      return parts.join(', ')
    }
  }

  return typeof address === 'string' && address.trim() ? address : ''
}

const resolveIssueLineOrderCode = (item: any): string => {
  const candidates: Array<unknown> = [
    item?.order_code,
    item?.order_no,
    item?.source_order_code,
    item?.source_order_no,
    item?.order?.code,
    item?.order?.name,
    item?.source_request_line_order_code,
    item?.source_request_line_order_no,
    item?.source_request_line?.order_code,
    item?.source_request_line?.order_no,
    item?.source_request_line?.order?.code,
    item?.source_request_line?.material_request?.order?.code,
  ]

  for (const candidate of candidates) {
    const value = String(candidate || '').trim()
    if (value) return value
  }

  return '-'
}

export const buildPrintTemplateContext = (orderData: PrintOrderData, settings: PrintOrderSettings) => {
  const today = new Date()
  let itemIndex = 0
  let issueItemIndex = 0
  const itemsRows = (orderData.items || [])
    .map(
      (item) => {
        if (item.isGroup) {
          return `<tr>
        <td colspan="7" style="border: 1px solid #ddd; padding: 6px; font-weight: bold; background-color: #f9fafb; white-space: normal; word-break: break-word;">${escapeHtml(item.groupName || item.product_name || '')}</td>
      </tr>`
        }
        itemIndex++
        return `<tr>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${itemIndex}</td>
        <td style="border: 1px solid #ddd; padding: 6px;">${escapeHtml(item.product_name || '')}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(item.color || '')}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${item.quantity || 0}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(item.unit_name || '')}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${formatCurrency(item.unit_price)}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${formatCurrency(item.total_price)}</td>
      </tr>`
      }
    )
    .join('')

  const issueItemsRows = (orderData.items || [])
    .map((item) => {
      if (item.isGroup) {
        return `<tr>
        <td colspan="6" style="border: 1px solid #ddd; padding: 6px; font-weight: bold; background-color: #f9fafb; white-space: normal; word-break: break-word;">${escapeHtml(item.groupName || item.product_name || '')}</td>
      </tr>`
      }
      issueItemIndex++
      return `<tr>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${issueItemIndex}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml((item as any).item_code || '')}</td>
        <td style="border: 1px solid #ddd; padding: 6px;">${escapeHtml(item.product_name || '')}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${item.quantity || 0}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(item.unit_name || '')}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(resolveIssueLineOrderCode(item))}</td>
      </tr>`
    })
    .join('')

  return {
    '{{title}}': settings.title,
    '{{invoice_title}}': settings.title,
    '{{company_logo}}': (() => {
      const logoUrl = getLocalLogoUrl(orderData.company?.logo)
      return logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="Logo" style="max-height: 80px; max-width: 200px;" />` : ''
    })(),
    '{{company_name}}': orderData.company?.name || settings.company_name,
    '{{company_name_upper}}': (orderData.company?.name || settings.company_name).toUpperCase(),
    '{{company_address}}': orderData.company?.address || settings.company_address,
    '{{company_phone}}': orderData.company?.phone || '',
    '{{company_email}}': orderData.company?.email || '',
    '{{order_code}}': orderData.order_code || '',
    '{{customer_name}}': orderData.customer?.name || '',
    '{{customer_phone}}': orderData.customer?.phone || '',
    '{{customer_address}}': formatOrderAddress(orderData),
    '{{sales_employee_name}}': orderData.sales_employee?.nickname || orderData.sales_employee?.name || '',
    '{{sales_employee_phone}}': settings.sales_staff_phone,
    '{{grand_total}}': formatCurrency(orderData.grand_total),
    '{{terms_title}}': settings.terms_title,
    '{{terms_html}}': linesToHtml(settings.terms_items, '** '),
    '{{warranty_title}}': settings.warranty_title,
    '{{warranty_description}}': settings.warranty_description,
    '{{warranty_extra_html}}': linesToHtml(settings.warranty_extra_items, '+ '),
    '{{non_warranty_title}}': settings.non_warranty_title,
    '{{non_warranty_html}}': linesToHtml(settings.non_warranty_items, '{index}. '),
    '{{footer_city}}': settings.footer_city_label,
    '{{current_day}}': String(today.getDate()),
    '{{current_month}}': String(today.getMonth() + 1),
    '{{current_year}}': String(today.getFullYear()),
    '{{current_date}}': today.toLocaleDateString('vi-VN'),
    '{{items_rows}}': itemsRows,
    '{{issue_items_rows}}': issueItemsRows,
    '{{issue_no}}': orderData.order_code || '',
    '{{order_no}}': (orderData as any).order_no || (orderData as any).order?.code || String(((orderData.items || [])[0] as any)?.order_code || ''),
    '{{issued_at}}': orderData.issued_at ? String(new Date(orderData.issued_at).toLocaleDateString('vi-VN')) : String(today.toLocaleDateString('vi-VN')),
    '{{warehouse_name}}': orderData.warehouse?.name || (orderData as any).warehouse_name || '',
    '{{picker_name}}': orderData.picker?.name || (orderData as any).picker_name || '',
    '{{created_by_name}}': (orderData as any).created_by?.name || (orderData as any).created_by_name || '',
    '{{total_qty}}': String(((orderData.items || []) as any[]).reduce((s, it) => s + Number(it.quantity || 0), 0)),
    '{{notes}}': escapeHtml(String(orderData.notes || '')),
  }
}

export const renderPrintTemplate = (template: string, context: Record<string, string>) => {
  return Object.entries(context).reduce((acc, [token, value]) => acc.replaceAll(token, value), template)
}

export const renderOrderPrint = async (orderData: PrintOrderData, settingsOverride?: PrintOrderSettings): Promise<string> => {
  const settings = settingsOverride || (await getPrintOrderSettings())
  const context = buildPrintTemplateContext(orderData, settings)
  return renderPrintTemplate(settings.template_html, context)
}

const formatNumberWithFallback = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-'
  const num = Number(value)
  if (!Number.isFinite(num)) return '-'
  return num.toLocaleString('vi-VN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const formatIntegerNumberWithFallback = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-'
  const num = Number(value)
  if (!Number.isFinite(num)) return '-'
  return formatIntegerNumber(num)
}

const formatDecimalNumberWithFallback = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-'
  const num = Number(value)
  if (!Number.isFinite(num)) return '-'
  const fixed = num.toFixed(2)
  const [intPart, decPart] = fixed.split('.')
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decPart ? `${formattedInt}.${decPart}` : formattedInt
}

const formatTextWithFallback = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

const resolveEmployeeName = (timesheet: TimesheetModel): string => {
  if (timesheet.employee_name) return timesheet.employee_name
  if (timesheet.employee && typeof timesheet.employee === 'object') {
    return timesheet.employee.name || timesheet.employee.code || ''
  }
  return formatTextWithFallback(timesheet.employee)
}

const resolveEmployeeCode = (timesheet: TimesheetModel): string => {
  if (timesheet.employee_code) return timesheet.employee_code
  if (timesheet.employee && typeof timesheet.employee === 'object') {
    return timesheet.employee.code || ''
  }
  return ''
}

const resolveSalaryConfigGroup = (timesheet: TimesheetModel): string => {
  if (timesheet.salary_config_group_name) return timesheet.salary_config_group_name
  if (timesheet.salary_config_group && typeof timesheet.salary_config_group === 'object') {
    return timesheet.salary_config_group.name || ''
  }
  return formatTextWithFallback(timesheet.salary_config_group)
}

const resolveCreatedBy = (timesheet: TimesheetModel): string => {
  if (!timesheet.created_by) return ''
  if (typeof timesheet.created_by === 'object') {
    return timesheet.created_by.name || timesheet.created_by.username || timesheet.created_by.id || ''
  }
  return timesheet.created_by
}

export const buildTimesheetPrintTemplateContext = (
  timesheet: TimesheetModel,
  settings: PrintTimesheetSalarySettings,
  index: number
): Record<string, string> => {
  const today = new Date()
  const companyName = settings.company_name || ''
  const timesheetRecord = timesheet as TimesheetModel & Record<string, unknown>
  const salaryByWorkingDays = timesheetRecord.salary_by_working_days ?? timesheet.salary_by_work_days
  const totalOvertimeHours = timesheetRecord.total_overtime_hours ?? timesheet.overtime_hours
  const totalSalary = timesheetRecord.total_salary ?? timesheet.total_salary_and_bonus
  const numberExportFiles = timesheetRecord.number_export_files ?? timesheet.exported_file_orders_count

  return {
    '{{title}}': settings.title,
    '{{company_name}}': companyName,
    '{{company_name_upper}}': companyName.toUpperCase(),
    '{{company_address}}': settings.company_address || '',
    '{{footer_city}}': settings.footer_city_label || '',
    '{{current_day}}': String(today.getDate()),
    '{{current_month}}': String(today.getMonth() + 1),
    '{{current_year}}': String(today.getFullYear()),
    '{{current_date}}': today.toLocaleDateString('vi-VN'),
    '{{item_index}}': String(index + 1),
    '{{id}}': formatTextWithFallback(timesheet.id),
    '{{status}}': formatTextWithFallback(timesheet.status),
    '{{salary_year}}': formatTextWithFallback(timesheet.salary_year),
    '{{salary_month}}': formatTextWithFallback(timesheet.salary_month),
    '{{employee_name}}': formatTextWithFallback(resolveEmployeeName(timesheet)),
    '{{employee_code}}': formatTextWithFallback(resolveEmployeeCode(timesheet)),
    '{{employee_id}}': formatTextWithFallback(timesheet.employee_id),
    '{{salary_config_group}}': formatTextWithFallback(resolveSalaryConfigGroup(timesheet)),
    '{{days_off}}': formatNumberWithFallback(timesheet.days_off),
    '{{working_days}}': formatDecimalNumberWithFallback(timesheet.working_days),
    '{{bonus_days}}': formatDecimalNumberWithFallback(timesheet.bonus_days),
    '{{total_working_days}}': formatDecimalNumberWithFallback(timesheet.total_working_days),
    '{{base_salary}}': formatIntegerNumberWithFallback(timesheet.base_salary),
    '{{daily_salary}}': formatIntegerNumberWithFallback(timesheet.daily_salary),
    '{{salary_by_work_days}}': formatIntegerNumberWithFallback(timesheet.salary_by_work_days),
    '{{salary_by_working_days}}': formatIntegerNumberWithFallback(salaryByWorkingDays),
    '{{overtime_hours}}': formatDecimalNumberWithFallback(timesheet.overtime_hours),
    '{{total_overtime_hours}}': formatNumberWithFallback(totalOvertimeHours),
    '{{overtime_hour_salary}}': formatIntegerNumberWithFallback(timesheet.overtime_hour_salary),
    '{{overtime_salary}}': formatIntegerNumberWithFallback(timesheet.overtime_salary),
    '{{total_overtime_salary}}': formatIntegerNumberWithFallback(timesheet.total_overtime_salary),
    '{{total_allowance}}': formatIntegerNumberWithFallback(timesheet.total_allowance),
    '{{total_salary_and_bonus}}': formatIntegerNumberWithFallback(timesheet.total_salary_and_bonus),
    '{{total_salary}}': formatIntegerNumberWithFallback(totalSalary),
    '{{social_insurance_deduction}}': formatIntegerNumberWithFallback(timesheet.social_insurance_deduction),
    '{{deposit_salary_deduction}}': formatIntegerNumberWithFallback(timesheet.deposit_salary_deduction),
    '{{penalty_amount}}': formatIntegerNumberWithFallback(timesheet.penalty_amount),
    '{{salary_payment}}': formatIntegerNumberWithFallback(timesheet.salary_payment),
    '{{advance_deduction}}': formatIntegerNumberWithFallback(timesheet.advance_deduction),
    '{{debt_deduction}}': formatIntegerNumberWithFallback(timesheet.debt_deduction),
    '{{note}}': formatTextWithFallback(timesheet.note),
    '{{net_salary}}': formatIntegerNumberWithFallback(timesheet.net_salary),
    '{{interest_rate_deposit}}': formatNumberWithFallback(timesheet.interest_rate_deposit),
    '{{previous_month_deposit_total}}': formatIntegerNumberWithFallback(timesheet.previous_month_deposit_total),
    '{{current_month_deposit_total}}': formatIntegerNumberWithFallback(timesheet.current_month_deposit_total),
    '{{interest_amount}}': formatIntegerNumberWithFallback(timesheet.interest_amount),
    '{{deposit_refund_amount}}': formatIntegerNumberWithFallback(timesheet.deposit_refund_amount),
    '{{revenue_product_month}}': formatIntegerNumberWithFallback(timesheet.revenue_product_month),
    '{{commission_amount}}': formatIntegerNumberWithFallback(timesheet.commission_amount),
    '{{export_file_bonus}}': formatIntegerNumberWithFallback(timesheet.export_file_bonus),
    '{{exported_file_orders_count}}': formatNumberWithFallback(timesheet.exported_file_orders_count),
    '{{number_export_files}}': formatNumberWithFallback(numberExportFiles),
    '{{kpi_bonus}}': formatIntegerNumberWithFallback(timesheet.kpi_bonus),
    '{{responsibility_allowance}}': formatIntegerNumberWithFallback(timesheet.responsibility_allowance),
    '{{lunch_allowance}}': formatIntegerNumberWithFallback(timesheet.lunch_allowance),
    '{{site_allowance}}': formatIntegerNumberWithFallback(timesheet.site_allowance),
    '{{created_at}}': formatTextWithFallback(timesheet.created_at),
    '{{updated_at}}': formatTextWithFallback(timesheet.updated_at),
    '{{created_by}}': formatTextWithFallback(resolveCreatedBy(timesheet)),
  }
}

export const renderTimesheetPrint = async (
  timesheets: TimesheetModel[],
  settingsOverride?: PrintTimesheetSalarySettings
): Promise<string[]> => {
  const settings = settingsOverride || (await getPrintTimesheetSalarySettings())
  return timesheets.map((timesheet, index) => {
    const context = buildTimesheetPrintTemplateContext(timesheet, settings, index)
    return renderPrintTemplate(settings.template_html, context)
  })
}

export const buildRevenueOrdersTableHtml = (orders: RevenueOrderItem[]): string => {
  const safeOrders = Array.isArray(orders) ? orders : []

  const rows = safeOrders
    .map((order, index) => {
      return `<tr>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${index + 1}</td>
        <td style="border: 1px solid #ddd; padding: 6px;">${escapeHtml(formatTextWithFallback(order.code))}</td>
        <td style="border: 1px solid #ddd; padding: 6px;">${escapeHtml(formatTextWithFallback(order.customer))}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(formatTextWithFallback(order.sale_date))}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(formatTextWithFallback(order.completion_date))}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${formatNumberWithFallback(order.revenue_without_vat)}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${formatNumberWithFallback(order.revenue_with_vat)}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${escapeHtml(formatTextWithFallback(order.reward_date))}</td>
      </tr>`
    })
    .join('')

  return `<table style="width: 100%; border-collapse: collapse; margin-bottom: 14px;">
    <thead>
      <tr>
        <th style="border: 1px solid #ddd; padding: 6px;">STT</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Mã đơn hàng</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Tên khách hàng</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Ngày bán hàng</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Ngày hoàn thành</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Doanh thu chưa VAT</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Doanh thu có VAT</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Ngày tính thưởng</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>`
}

export const buildRevenueOrdersTableHtmlFromApi = (orders: any[]): string => {
  if (!orders || orders.length === 0) {
    return '<p>Không có dữ liệu đơn hàng</p>';
  }

  const rows = orders
    .map((order) => {
      const orderCode = escapeHtml(order.order_code || '');
      const customerName = escapeHtml(order.customer_name || '');
      const totalPrice = formatNumberWithFallback(order.total_price);
      const totalPriceNotTax = formatNumberWithFallback(order.total_price_not_tax);
      const salesEmployeeName = escapeHtml(order.sales_employee_name || '');

      return `<tr>
        <td style="border: 1px solid #ddd; padding: 6px;">${orderCode}</td>
        <td style="border: 1px solid #ddd; padding: 6px;">${customerName}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${totalPrice}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: right;">${totalPriceNotTax}</td>
        <td style="border: 1px solid #ddd; padding: 6px;">${salesEmployeeName}</td>
      </tr>`;
    })
    .join('');

  return `<table style="width: 100%; border-collapse: collapse; margin-bottom: 14px;">
    <thead>
      <tr>
        <th style="border: 1px solid #ddd; padding: 6px;">Mã đơn hàng</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Khách hàng</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Doanh số</th>
        <th style="border: 1px solid #ddd; padding: 6px;">Doanh số trừ VAT</th>
        <th style="border: 1px solid #ddd; padding: 6px;">NV Bán hàng</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>`;
};



export const buildRevenuePrintTemplateContext = (
  timesheet: TimesheetModel,
  settings: PrintRevenueSettings,
  index: number
): Record<string, string> => {
  const today = new Date()
  const companyName = settings.company_name || ''
  const orders = timesheet.list_revenue_order_in_month || []
  const ordersRevenue = timesheet.orders_revenue
  const revenueOrders = ordersRevenue?.orders || []

  return {
    '{{title}}': settings.title,
    '{{company_name}}': companyName,
    '{{company_name_upper}}': companyName.toUpperCase(),
    '{{company_address}}': settings.company_address || '',
    '{{footer_city}}': settings.footer_city_label || '',
    '{{item_index}}': String(index + 1),
    '{{current_day}}': String(today.getDate()),
    '{{current_month}}': String(today.getMonth() + 1),
    '{{current_year}}': String(today.getFullYear()),
    '{{current_date}}': today.toLocaleDateString('vi-VN'),
    '{{salary_month}}': formatTextWithFallback(timesheet.salary_month),
    '{{salary_year}}': formatTextWithFallback(timesheet.salary_year),
    '{{employee_name}}': formatTextWithFallback(resolveEmployeeName(timesheet)),
    '{{employee_code}}': formatTextWithFallback(resolveEmployeeCode(timesheet)),
    '{{revenue_product_month}}': formatIntegerNumberWithFallback(timesheet.revenue_product_month),
    '{{commission_amount}}': formatIntegerNumberWithFallback(timesheet.commission_amount),
    '{{orders_table}}': buildRevenueOrdersTableHtml(orders),
    '{{orders_revenue_table}}': buildRevenueOrdersTableHtmlFromApi(revenueOrders),
    '{{total_price}}': formatIntegerNumberWithFallback(ordersRevenue?.total_price),
    '{{total_price_not_tax}}': formatIntegerNumberWithFallback(ordersRevenue?.total_price_not_tax),
    '{{order_revenue_without_vat}}': formatTextWithFallback('revenue_without_vat'),
    '{{order_revenue_with_vat}}': formatTextWithFallback('revenue_with_vat'),
  }
}

export const renderRevenuePrint = async (
  timesheets: TimesheetModel[],
  settingsOverride?: PrintRevenueSettings
): Promise<string[]> => {
  const settings = settingsOverride || (await getPrintRevenueSettings())
  return timesheets.map((timesheet, index) => {
    const context = buildRevenuePrintTemplateContext(timesheet, settings, index)
    return renderPrintTemplate(settings.template_html, context)
  })
}
